import React, { memo, useState } from 'react';
import 'react-loading-skeleton/dist/skeleton.css';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { Header } from './Header';
import { useExtractionTransformationTableHelper } from './hooks/useExtractionTransformationTableHelper';
import Transformation from './Transformation';
import Extraction from './Extraction';
import { Flex } from '@mantine/core';

const Renderers = {
  transformation: Transformation,
  extraction: Extraction
};

export default memo(function GridTable({
  quarter,
  year,
  project,
  file,
  onSelectExtractCategory,
  sheet,
  response,
  isLoading,
  funds,
  isIndividualFund,
  category,
  onChangeCategory,
  action,
  onChangeAction,
  onReplicateAutoExtract,
  isLoadingAutoExtract,
  pdfViewMode,
  onChangePdfViewMode,
  onSearch,
  foundItems,
  onSearchToUp,
  onSearchToDown,
  activeSearchedIndex,
  categories,
  refetchCategories,
  term,
  activeFundChildValue,
  templateType,
  onEnableValue,
  setActiveFundChildValue,
  selectedFund,
  setSelectedFund,
  setPickedChildFund,
  pickedChildFund,
  setPickedFund,
  pickedFund,
  activeTab,
  changeActiveTab,
  onChangeFunds,
  currentExtract,
  onClearSearch,
  isLookup,
  excelTableRef,
  onFocusLocation,
  fundMappings,
  periodId,
  refetchFundMappings,
  isFundLevel,
  refetchSheet,
  hasChanges,
  openPopup,
  onRedo,
  onUndo,
  isUndoDisabled,
  isRedoDisabled,
  zoomIn,
  zoomOut,
  extractFile,
  zoomLevel,
  footerIndex,
  headerIndex,
  onChangeHeaderFooter,
  onChangeTableStructure,
  foundCell,
  transformationData,
  setTransformationData,
  openCustomTableDrawer,
  openUpdateCustomTableDrawer,
  onSelectCustomTable,
  isLockedPeriod,
  reportType,
  transformationProgressStatus,
  extractedTransformationTable,
  onChangeExtractedTransformationTable,
  pdfRegionSelections,
  onSavePdfRegionSelection,
  onDeletePdfRegionSelection,
  isSavingPdfRegionSelection,
  isDeletingPdfRegionSelection,
  isLoadingPdfRegionPreview,
  canManageWorkflow = true
}) {
  const [width, setWidth] = useState(530);

  const {
    error,
    mutatedFunds,
    focusedCell,
    selectedColumn,
    selectedLookupTable,
    onSelectFund,
    onSelectTable,
    onSelectColumn,
    onClearFundValues,
    onSelectionStructuredCells,
    onSelectionUnStructuredCells,
    onFullSelectionStructuredCells,
    onClearValuesColumn,
    onDeleteSelectedCell,
    onPickChildFund,
    onPickFund,
    onDeleteRow,
    onFocusedCell,
    onFixRow,
    onFixColumn,
    onFixRowAndColumn,
    rowPinning,
    columnPinning
  } = useExtractionTransformationTableHelper({
    reportType,
    headerIndex,
    data: response?.data,
    funds,
    pickedFund,
    pickedChildFund,
    onClearSearch,
    isLookup,
    fundMappings,
    setPickedChildFund,
    setPickedFund,
    onChangeFunds,
    selectedFund,
    setSelectedFund,
    setActiveFundChildValue,
    activeFundChildValue,
    response,
    isIndividualFund,
    footerIndex
  });

  const handleFullScreen = useFullScreenHandle();

  const Renderer = Renderers[activeTab];
  const isPdfGeneratedTable = file?.format === 'pdf';
  const headerIsStructured = response?.is_structured;
  const headerStartIndex = headerIndex;
  const headerEndIndex = footerIndex;
  const hideTableType = activeTab === 'transformation' || isLockedPeriod;

  const headerComponent = (
    <Header
      file={file}
      sheet={sheet}
      quarter={quarter}
      year={year}
      project={project}
      periodId={periodId}
      onFixColumn={onFixColumn}
      onFixRow={onFixRow}
      focusedCell={focusedCell}
      isStructured={headerIsStructured}
      onSearch={onSearch}
      foundItems={foundItems}
      onSearchToUp={onSearchToUp}
      onSearchToDown={onSearchToDown}
      activeSearchedIndex={activeSearchedIndex}
      term={term}
      onFixRowAndColumn={onFixRowAndColumn}
      fixedColumnId={columnPinning.left.length}
      fixedRowId={rowPinning.top.length}
      isFundLevel={isFundLevel}
      hideTableType={hideTableType}
      disableTableTypeToggle={isPdfGeneratedTable}
      isFullScreen={handleFullScreen.active}
      onRedo={onRedo}
      onUndo={onUndo}
      onZoomIn={zoomIn}
      onZoomOut={zoomOut}
      onFullScreen={() => {
        handleFullScreen.enter()?.catch(error => {
          console.log(`fullscreen enter ${error}`);
        });
      }}
      onBackFullScreen={() => {
        handleFullScreen.exit()?.catch(error => {
          console.log(`fullscreen exit ${error}`);
        });
      }}
      isUndoDisabled={isUndoDisabled}
      isRedoDisabled={isRedoDisabled}
      footerIndex={headerEndIndex}
      headerIndex={headerStartIndex}
      totalDataLength={response?.data?.length}
      onChangeHeaderFooter={onChangeHeaderFooter}
      onChangeTableStructure={onChangeTableStructure}
    />
  );

  return (
    <FullScreen handle={handleFullScreen}>
      <Flex
        id="grid-table-root"
        h={handleFullScreen.active ? '100vh' : 'calc(100vh - 280px)'}
        style={{
          overflow: 'hidden'
        }}
        isFullScreen={handleFullScreen.active}
      >
        <Renderer
          id="grid-table-renderer"
          context={{
            width,
            isLoading,
            hasChanges,
            openPopup,
            selectedLookupTable,
            isStructured: response?.is_structured,
            selectedFund,
            onSelectFund,
            onChangeAction,
            action,
            pickedFund,
            onPickFund,
            onSelectTable,
            onPickChildFund,
            pickedChildFund,
            onEnableValue,
            activeFundChildValue,
            onDeleteSelectedCell, // TODO: remove to Extraction
            categories,
            funds: mutatedFunds,
            onChangeCategory,
            category,
            onSearch, // TODO: remove to Extraction
            setPickedFund,
            setSelectedFund,
            error,
            onSelectColumn, // TODO: remove to Extraction
            selectedColumn, // TODO: remove to Extraction
            isLookup,
            onClearValuesColumn, // TODO: remove to Extraction
            onClearFundValues, // TODO: remove to Extraction
            onFocusLocation, // TODO: remove to Extraction
            onDeleteRow, // TODO: remove to Extraction
            sheetId: sheet?.id,
            sheetName: sheet?.name,
            fileId: file?.id,
            fileFormat: file?.format,
            setWidth,
            fundMappings,
            refetchFundMappings,
            templateType,
            refetchSheet,
            isIndividualFund,
            footerIndex,
            headerIndex,
            extractId: currentExtract?.id,
            workflowId: project?.id,
            periodId: periodId,
            extractFile,
            onSelectExtractCategory,
            refetchCategories,
            rowPinning, // TODO: remove to Extraction/Transformation
            columnPinning, // TODO: remove to Extraction/Transformation
            term, // TODO: remove to Extraction/Transformation
            onSelectionUnStructuredCells, // TODO: remove to Extraction
            onSelectionStructuredCells, // TODO: remove to Extraction
            data: response?.data, // TODO: remove to Extraction
            zoomLevel,
            onFocusedCell, // TODO: remove to Extraction
            excelTableRef, // TODO: remove to Extraction
            foundCell, // TODO: remove to Extraction
            onReplicateAutoExtract,
            pdfViewMode,
            onChangePdfViewMode,
            onFullSelectionStructuredCells,
            transformationData,
            setTransformationData,
            isLoadingAutoExtract,
            headerComponent,
            openCustomTableDrawer,
            openUpdateCustomTableDrawer,
            onSelectCustomTable,
            year,
            workflowName: project?.name,
            periodName: project?.periods?.find(el => el.id === periodId)?.period,
            extractedTransformationTable,
            onChangeExtractedTransformationTable,
            pdfRegionSelections,
            tableCsv: response?.pdf_region_preview?.csv || '',
            onSavePdfRegionSelection,
            onDeletePdfRegionSelection,
            isSavingPdfRegionSelection,
            isDeletingPdfRegionSelection,
            isLoadingPdfRegionPreview,
            onFixRow,
            onFixColumn,
            transformationProgressStatus,
            changeActiveTab,
            canManageWorkflow,
            periodCategory: project?.periods?.find(el => el.id === periodId)?.period_category
          }}
        />
      </Flex>
    </FullScreen>
  );
});
